import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import { FiEye, FiEyeOff, FiRefreshCw } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import ThemeToggle from "../../Components/ThemeToggle";
import { makeRequest } from "../../_config/api";

export default function Login() {
  const [admin, setAdmin] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Forgot Password States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: Request OTP, 2: Reset Password
  const [forgotUser, setForgotUser] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleOtpDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);

    if (value && index < 5) {
      const nextInput = document.getElementById(`admin-otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`admin-otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdmin((prev) => ({ ...prev, [name]: value }));
  };

  const getFirstAllowedRoute = (user) => {
    if (user.role === "admin") return "/";

    const { role, leader, accessPages = [] } = user;

    const MENU_CONFIG = {
      property_team: [
        { name: "pending", to: "/properties/pending" },
        { name: "approved", to: "/properties/approved" },
        { name: "sold", to: "/properties/sold" },
        { name: "rejected", to: "/properties/rejected" },
        { name: "queries", to: "/properties/query" },
        { name: "categories", to: "/properties/categories" },
      ],
      loan_team: [
        { name: "loan_management", to: "/loans/listing" },
        { name: "queries", to: "/loans/queries" },
      ],
      services_team: [
        { name: "category_management", to: "/services/category" },
        { name: "service_booking", to: "/services/booking" },
        { name: "queries", to: "/services/Ajent" },
      ],
      support_team: [{ name: "queries", to: "/support/queries" }],
    };

    const roleMenu = MENU_CONFIG[role];

    if (!roleMenu) return "/";

    const firstValidPage = roleMenu.find((item) => {
      if (leader === true) return true;
      return accessPages.includes(item.name);
    });

    return firstValidPage ? firstValidPage.to : "/";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!admin.username.trim() || !admin.password) {
      showErrorToast("username and password are required");
      return;
    }

    setLoading(true);

    try {
      const { data, error, message } = await makeRequest(
        "post",
        "/admin/adminLogin",
        {
          userName: admin.username,
          password: admin.password,
        }
      );
      if (error) {
        showErrorToast(message || "Login failed");
        return;
      }
      // console.log("login : ", data)
      showSuccessToast("Login successful");

      const userData = {
        role: data.role,
        leader: data.leader,
        accessPages: data.accessPages,
        token: data.token,
        userId: data.userId,
      };

      sessionStorage.setItem("user", JSON.stringify(userData));

      // ✅ CHANGE HERE: Dynamic Navigation
      const redirectPath = getFirstAllowedRoute(userData);
      navigate(redirectPath);
    } catch (error) {
      console.error("Login failed:", error);
      showErrorToast(error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!forgotUser.trim()) {
      showErrorToast("Please enter your username or email");
      return;
    }
    setForgotLoading(true);
    setTimeout(() => {
      showSuccessToast("OTP sent successfully!");
      setForgotStep(2);
      setForgotLoading(false);
    }, 600);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const enteredOtp = otpDigits.join("");
    if (enteredOtp.length < 6) {
      showErrorToast("Please enter the 6-digit OTP code");
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      showErrorToast("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }
    setForgotLoading(true);
    setTimeout(() => {
      showSuccessToast("Password reset successfully! Please login with your new password.");
      setShowForgotModal(false);
      setForgotStep(1);
      setAdmin((prev) => ({ ...prev, username: forgotUser.trim(), password: "" }));
      setForgotUser("");
      setOtpDigits(["", "", "", "", "", ""]);
      setNewPassword("");
      setConfirmPassword("");
      setForgotLoading(false);
    }, 600);
  };

  return (
    <div className="min-h-screen flex bg-[#F8F8EC] dark:bg-[#121212] transition-all">
      <ToastContainer />

      {/* Left Side: Modern Property Showcase */}
      <div className="hidden md:flex md:w-1/2 lg:w-[55%] relative bg-[#F8F8EC] dark:bg-[#121212] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=100&w=2500"
          alt="Coastal City Skyline"
          className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[10s] ease-in-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent flex flex-col justify-end p-10 lg:p-14 text-white z-10">
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight mb-3">
            Your Dream Property,<br />Just One Step Away
          </h2>
          <p className="text-gray-200 text-sm lg:text-base font-medium max-w-md">
            Discover amazing homes tailored just for you.
          </p>
        </div>
      </div>

      {/* Right Side: Form Container */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 relative overflow-y-auto">

        {/* Mobile Theme Toggle */}
        <div className="absolute top-6 right-6 md:hidden">
          <ThemeToggle />
        </div>


        <div className="max-w-md w-full bg-white dark:bg-[#1E1E1E] p-8 sm:p-10 rounded-2xl shadow-xl border border-gray-100 dark:border-[#2D2D2D]">

          <div className="mb-8 text-center">
            <div className="w-14 h-14 bg-gradient-to-br from-[#24ADD7] to-[#1a85a6] text-white rounded-2xl flex items-center justify-center mb-5 mx-auto shadow-lg shadow-[#24ADD7]/30">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h1 className="text-3xl font-black text-[#1f2937] dark:text-white tracking-tight">
              Welcome Back
            </h1>
            <p className="text-gray-500 dark:text-[#BFBFBF] mt-2 font-medium text-sm">
              Please enter your details to sign in.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Username or Email
              </label>
              <input
                type="text"
                name="username"
                value={admin.username}
                onChange={handleInputChange}
                placeholder="Enter username"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#2D2D2D]
                           bg-white dark:bg-[#121212]
                           text-gray-800 dark:text-white
                           focus:outline-none focus:border-[#24ADD7] dark:focus:border-[#24ADD7]
                           transition-colors text-sm font-medium"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={admin.password}
                onChange={handleInputChange}
                placeholder="Enter password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-[#2D2D2D]
                           bg-white dark:bg-[#121212]
                           text-gray-800 dark:text-white
                           focus:outline-none focus:border-[#24ADD7] dark:focus:border-[#24ADD7]
                           transition-colors text-sm font-medium pr-12"
              />

              <div
                className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#24ADD7] transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </div>
            </div>

            {/* Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center items-center rounded-xl
                           px-4 py-3.5 text-white font-bold text-base transition-all duration-300
                           shadow-md hover:shadow-lg
                          ${loading
                    ? "bg-[#0ea5e9]/80 cursor-not-allowed opacity-70"
                    : "bg-[#0ea5e9] hover:bg-[#0284c7]"
                  }`}
              >
                {loading ? (
                  <>
                    <FiRefreshCw className="animate-spin h-5 w-5 mr-3" />
                    Authenticating...
                  </>
                ) : (
                  "Sign In to Dashboard"
                )}
              </button>
            </div>
          </form>

        </div>

      </div>
    </div>
  );
}
